import {
  ActionIcon,
  Box,
  Button,
  Center,
  Flex,
  Group,
  Loader,
  Menu,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import {
  IconCheck,
  IconDotsVertical,
  IconPencil,
  IconPencilPlus,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useCountdown } from 'src/common/useCountdown';
import { useAppContainerContext } from '../AppContainerContext';
import { useSiesta } from '../SiestaProvider';
import classes from './ChatsView.module.css';
import { ConversationStatusBadge } from './ConversationStatusBadge';
import { ConversationListItem } from './types';
import { useConversations } from './useConversations';
import { useDeleteConversation } from './useDeleteConversation';
import { useRenameConversation } from './useRenameConversation';
import { useOpenConversation } from './useOpenConversation';

const UNDO_DELETE_SECONDS = 5;

export function ChatsView({ onNewChat }: { onNewChat: () => void }) {
  const { discardChat, chatScope } = useSiesta();
  const listClientId = chatScope.familyModeActive ? undefined : (chatScope.clientId ?? undefined);
  const listFamilyId = chatScope.familyModeActive ? (chatScope.familyId ?? undefined) : undefined;
  const { data: conversations = [], isLoading } = useConversations(listClientId, listFamilyId);
  const { renameConversation } = useRenameConversation();
  const openConversation = useOpenConversation();
  const { deleteConversation } = useDeleteConversation();
  const { conversationId } = useAppContainerContext();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [pendingIds, setPendingIds] = useState<Set<string>>(() => new Set());

  const startDelete = (id: string) => {
    setPendingIds(previous => new Set(previous).add(id));
  };

  const cancelDelete = (id: string) => {
    setPendingIds(previous => {
      const next = new Set(previous);
      next.delete(id);
      return next;
    });
  };

  const confirmDelete = (id: string) => {
    queryClient.setQueryData<ConversationListItem[]>(
      ['conversations', { clientId: listClientId, familyId: listFamilyId }],
      previous => (previous ? previous.filter(conversation => conversation.id !== id) : previous),
    );
    deleteConversation(id);
    if (id === conversationId) {
      discardChat();
    }
    cancelDelete(id);
  };

  const query = search.trim().toLowerCase();
  const filtered = query
    ? conversations.filter(conversation => (conversation.title ?? '').toLowerCase().includes(query))
    : conversations;

  return (
    <Flex direction="column" h="100%" gap="md">
      <Group gap="sm" wrap="nowrap" align="center">
        <Box flex={1}>
          <TextInput
            w="100%"
            value={search}
            onChange={event => setSearch(event.currentTarget.value)}
            placeholder="Search chats"
            leftSection={<IconSearch size={18} />}
            radius="xl"
            size="md"
            classNames={{ input: classes.searchInput, section: classes.searchSection }}
          />
        </Box>
        <ActionIcon variant="subtle" size="lg" aria-label="Start a new chat" onClick={onNewChat}>
          <IconPencilPlus size={22} color="var(--mantine-color-white)" />
        </ActionIcon>
      </Group>

      <Box flex={1} mih={0} style={{ overflowY: 'auto' }}>
        <ChatsList
          conversations={filtered}
          isLoading={isLoading}
          hasAny={conversations.length > 0}
          pendingIds={pendingIds}
          onRename={renameConversation}
          onOpen={openConversation}
          onStartDelete={startDelete}
          onConfirmDelete={confirmDelete}
          onCancelDelete={cancelDelete}
        />
      </Box>
    </Flex>
  );
}

function ChatsList({
  conversations,
  isLoading,
  hasAny,
  pendingIds,
  onRename,
  onOpen,
  onStartDelete,
  onConfirmDelete,
  onCancelDelete,
}: {
  conversations: ConversationListItem[];
  isLoading: boolean;
  hasAny: boolean;
  pendingIds: Set<string>;
  onRename: (id: string, title: string) => void;
  onOpen: (id: string) => void;
  onStartDelete: (id: string) => void;
  onConfirmDelete: (id: string) => void;
  onCancelDelete: (id: string) => void;
}) {
  if (isLoading) {
    return (
      <Center h="100%">
        <Loader color="white" />
      </Center>
    );
  }

  if (conversations.length === 0) {
    if (hasAny) {
      return (
        <Flex direction="column" align="center" justify="center" h="100%" px="xl">
          <Text size="textXl" fw={FONT_WEIGHTS.medium} c="white" ta="center">
            No results
          </Text>
          <Text size="textSm" className={classes.dimmed} ta="center" mt="xs">
            Make sure the query is written correctly or try modifying it
          </Text>
        </Flex>
      );
    }
    return (
      <Flex direction="column" align="center" justify="center" h="100%" px="xl">
        <Text size="textXl" fw={FONT_WEIGHTS.medium} c="white" ta="center">
          Your chats will appear here
        </Text>
        <Text size="textSm" className={classes.dimmed} ta="center" mt="xs">
          Ask Siesta anything about your clients - finances, meeting prep, document questions, and
          more.
        </Text>
      </Flex>
    );
  }

  return (
    <Stack gap={0}>
      {conversations.map(conversation => (
        <ConversationRow
          key={conversation.id}
          conversation={conversation}
          pending={pendingIds.has(conversation.id)}
          onRename={onRename}
          onOpen={onOpen}
          onStartDelete={onStartDelete}
          onConfirmDelete={onConfirmDelete}
          onCancelDelete={onCancelDelete}
        />
      ))}
    </Stack>
  );
}

function ConversationRow({
  conversation,
  pending,
  onRename,
  onOpen,
  onStartDelete,
  onConfirmDelete,
  onCancelDelete,
}: {
  conversation: ConversationListItem;
  pending: boolean;
  onRename: (id: string, title: string) => void;
  onOpen: (id: string) => void;
  onStartDelete: (id: string) => void;
  onConfirmDelete: (id: string) => void;
  onCancelDelete: (id: string) => void;
}) {
  const { flagEnabledSiestaAsyncGeneration } = useFlags();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(conversation.title ?? '');
  const skipCommitRef = useRef(false);

  const commit = () => {
    if (skipCommitRef.current) {
      skipCommitRef.current = false;
      setEditing(false);
      return;
    }
    const next = draft.trim();
    if (next && next !== (conversation.title ?? '')) {
      onRename(conversation.id, next);
    }
    setEditing(false);
  };

  return (
    <Flex align="center" mih={40}>
      {pending && (
        <PendingDeleteRow
          title={conversation.title ?? 'Untitled chat'}
          onConfirm={() => onConfirmDelete(conversation.id)}
          onCancel={() => onCancelDelete(conversation.id)}
        />
      )}
      {!pending && editing && (
        <Group w="100%" gap="xs" wrap="nowrap" align="center">
          <Box flex={1}>
            <TextInput
              w="100%"
              value={draft}
              onChange={event => setDraft(event.currentTarget.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') event.currentTarget.blur();
                if (event.key === 'Escape') {
                  skipCommitRef.current = true;
                  event.currentTarget.blur();
                }
              }}
              onBlur={commit}
              autoFocus
              radius="xl"
              size="sm"
              classNames={{ input: classes.searchInput }}
            />
          </Box>
          <ActionIcon
            variant="subtle"
            size="sm"
            aria-label="Save"
            onMouseDown={event => event.preventDefault()}
            onClick={commit}
          >
            <IconCheck size={18} color="var(--mantine-color-white)" />
          </ActionIcon>
        </Group>
      )}
      {!pending && !editing && (
        <Flex w="100%" justify="space-between" align="center" gap="md">
          <Text
            size="sm"
            c="white"
            lineClamp={1}
            onClick={() => onOpen(conversation.id)}
            style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
          >
            {conversation.title ?? 'Untitled chat'}
          </Text>
          <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
            {flagEnabledSiestaAsyncGeneration && (
              <ConversationStatusBadge
                turnState={conversation.turnState}
                firstTokenAt={conversation.firstTokenAt}
              />
            )}
            {conversation.updatedAt && (
              <Text size="xs" className={classes.dimmed}>
                {formatLastUpdated(conversation.updatedAt)}
              </Text>
            )}
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon variant="subtle" size="sm" aria-label="Chat actions">
                  <IconDotsVertical size={18} color="var(--mantine-color-white)" />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconPencil size={16} />}
                  onClick={() => {
                    setDraft(conversation.title ?? '');
                    setEditing(true);
                  }}
                >
                  Rename
                </Menu.Item>
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={16} />}
                  onClick={() => onStartDelete(conversation.id)}
                >
                  Delete
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Flex>
      )}
    </Flex>
  );
}

function PendingDeleteRow({
  title,
  onConfirm,
  onCancel,
}: {
  title: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const countdown = useCountdown(UNDO_DELETE_SECONDS, true, onConfirm);

  return (
    <Flex
      w="100%"
      align="center"
      justify="space-between"
      gap="md"
      px="md"
      py="xxs"
      className={classes.pendingPill}
    >
      <Text size="sm" lineClamp={1} className={classes.dimmed} style={{ flex: 1, minWidth: 0 }}>
        {title}
      </Text>
      <Button variant="subtle" size="compact-sm" onClick={onCancel} style={{ flexShrink: 0 }}>
        Undo ({countdown})
      </Button>
    </Flex>
  );
}

function formatLastUpdated(value: string): string {
  const then = dayjs(value);
  const now = dayjs();
  const minutes = now.diff(then, 'minute');
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = now.diff(then, 'hour');
  if (hours < 24) return `${hours}h ago`;
  const days = now.diff(then, 'day');
  if (days < 7) return `${days}d ago`;
  return then.format('DD MMM YYYY');
}
